import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { leadApi } from "../../pages/services/leadService";
import { useTablePreferences } from "../utils/useTablePreferences";
import { drainQueue } from "./LeadBulkActions";
import { ChannelBadge, MESSAGE_KIND, errorText, fmtDateTime, refreshUntilDone } from "./leadUtils";
import "../../styles/Leads.css";

// Every message the app has sent to a lead (and every failed attempt), latest
// first. Filters live in the address bar, like the lead list's.
const PAGE_SIZES = [10, 20, 50, 100, 500];
const FILTER_KEYS = ["channel", "result", "kind", "from", "to", "search"];
const RESULTS = [
  ["", "Any result"],
  ["sent", "Sent"],
  ["delivered", "Delivered or read"],
  ["read", "Read"],
  ["anyFailure", "Failed or not delivered"],
  ["failed", "Failed to send"],
  ["undelivered", "Sent, not delivered"],
];

// What happened to it: the send, then (WhatsApp / email through UraanWapp) its delivery.
const outcome = (m) => {
  if (m.status === "failed") return { s: "failed", label: "Failed to send" };
  // A resend still on its way (shown with the message it resends).
  if (m.status === "scheduled" || m.status === "sending") return { s: "scheduled", label: m.retryAt ? "Queued — UraanWapp busy, trying again" : "Sending" };
  if (m.status === "skipped" || m.status === "cancelled") return { s: "skipped", label: "Not sent" };
  const d = m.deliveryStatus;
  if (d === "failed") return { s: "failed", label: "Not delivered" };
  if (d === "read") return { s: "sent", label: "Read" };
  if (d === "delivered") return { s: "sent", label: "Delivered" };
  return { s: "sent", label: "Sent" };
};

const LeadNotificationLog = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(params.get("search") || "");
  const [reloadKey, setReloadKey] = useState(0);
  // The running action (refresh / resend) and what it says: { busy, tone, text }.
  const [job, setJob] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const page = Math.max(1, parseInt(params.get("page"), 10) || 1);
  const { rowsPerPage: storedRows, setRowsPerPage } = useTablePreferences("leadLogPrefs", 20);
  const rowsPerPage = PAGE_SIZES.includes(Number(storedRows)) ? Number(storedRows) : 20;

  const filters = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""])), [params]);
  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    next.delete("page");
    setParams(next);
  };

  useEffect(() => {
    setLoading(true);
    setError("");
    leadApi.notificationLog({ ...filters, page, limit: rowsPerPage })
      .then((r) => { setRows(r.data); setTotal(r.total); })
      .catch((e) => setError(errorText(e)))
      .finally(() => setLoading(false));
  }, [filters, page, rowsPerPage, reloadKey]);

  const filterText = FILTER_KEYS.some((k) => filters[k]) ? " matching the filters" : "";

  // Ask UraanWapp for the current delivery of every sent message (the filters apply).
  const refreshStatus = async () => {
    setJob({ busy: true, tone: "info", text: "Checking delivery with UraanWapp…" });
    try {
      const t = await refreshUntilDone((since) => leadApi.refreshLog(filters, since),
        (p) => setJob({ busy: true, tone: "info", text: `Checking delivery with UraanWapp… ${p.checked.toLocaleString("en-IN")} checked${p.remaining ? `, ${p.remaining.toLocaleString("en-IN")} left` : ""}` }));
      setJob({ tone: "success", text: t.checked
        ? `Delivery checked for ${t.checked.toLocaleString("en-IN")} message${t.checked === 1 ? "" : "s"}${filterText}: ${t.changed.toLocaleString("en-IN")} changed.${t.remaining ? ` ${t.remaining.toLocaleString("en-IN")} not reached — the scheduler checks them in its next runs.` : ""}`
        : `Every sent message${filterText} already has its final status (read, failed) or nothing to check.` });
    } catch (e) {
      setJob({ tone: "error", text: `Refresh stopped: ${errorText(e)}` });
    }
    setReloadKey((k) => k + 1);
  };

  // Send one failed message again, now — the same as Resend on the lead's page.
  // m: the row; its newest attempt (the row itself, or its latest resend) is what is sent.
  const resendOne = async (m) => {
    const target = m.resend || m;
    const channel = m.channel === "email" ? "email" : "WhatsApp";
    const again = m.resend ? `\n\nIt was already sent again on ${fmtDateTime(m.resend.at || m.resentAt)} — ${outcome(m.resend).label.toLowerCase()}.` : "";
    if (!window.confirm(`Send "${m.title || "this message"}" to ${m.lead?.fullName || "this lead"} again by ${channel}?${again}`)) return;
    setResendingId(m._id);
    try {
      const r = await leadApi.resendMessage(target._id);
      const who = `${m.title || "Message"} to ${m.lead?.fullName || "the lead"}`;
      if (r?.status === "sent") setJob({ tone: "success", text: `Sent again: ${who} (${channel}).` });
      else if (r?.status === "scheduled") setJob({ tone: "info", text: `UraanWapp is busy — ${who} is queued and will be tried again in a few minutes.` });
      else setJob({ tone: "error", text: `Not sent: ${who} — ${r?.error || r?.skipReason || "failed again"}.` });
    } catch (e) {
      setJob({ tone: "error", text: `Resend failed: ${errorText(e)}` });
    } finally {
      setResendingId(null);
      setReloadKey((k) => k + 1);
    }
  };

  // Send every failed message (the filters apply) again: count, confirm, queue, send.
  const resendAll = async () => {
    setJob({ busy: true, tone: "info", text: "Counting the failed messages…" });
    try {
      const c = await leadApi.resendFailed(filters, true);
      const held = [
        c.payment ? `${c.payment} payment link${c.payment === 1 ? "" : "s"} (resend those from the lead's page)` : "",
        c.leadDeleted ? `${c.leadDeleted} of deleted leads` : "",
        c.duplicates ? `${c.duplicates} repeat${c.duplicates === 1 ? "" : "s"} of the same message to the same lead (sent once)` : "",
      ].filter(Boolean).join("; ");
      if (!c.resend) { setJob({ tone: "info", text: `No failed messages to resend${filterText}.${held ? ` Left out: ${held}.` : ""}` }); return; }
      const what = [c.failedToSend ? `${c.failedToSend} that failed to send` : "", c.notDelivered ? `${c.notDelivered} sent but not delivered` : ""].filter(Boolean).join(" and ");
      if (!window.confirm(`Send ${c.resend} failed message${c.resend === 1 ? "" : "s"}${filterText} again (${what})?${held ? `\n\nLeft out: ${held}.` : ""}${c.capped ? "\n\nOnly the latest 2,000 are taken this time." : ""}`)) { setJob(null); return; }
      const q = await leadApi.resendFailed(filters, false);
      setJob({ busy: true, tone: "info", text: `Sending ${q.queued} message${q.queued === 1 ? "" : "s"} again…` });
      const s = await drainQueue((p) => setJob({ busy: true, tone: "info", text: `Sending again… ${p.sent} sent${p.failed ? `, ${p.failed} failed` : ""}${p.due ? `, ${p.due} left` : ""}` }));
      setJob({ tone: s.failed ? "error" : "success", text: `Sent again: ${s.sent}${s.failed ? ` · failed again: ${s.failed}` : ""}${s.skipped ? ` · skipped: ${s.skipped} (the step was removed or turned off in the course settings)` : ""}${s.retrying ? ` · ${s.retrying} to be tried again in a few minutes (UraanWapp busy)` : ""}${s.due ? ` · ${s.due} still queued — the scheduler sends them within a few minutes` : ""}.` });
    } catch (e) {
      setJob({ tone: "error", text: `Resend stopped: ${errorText(e)}` });
    }
    setReloadKey((k) => k + 1);
  };

  // The search box filters after a short pause, not on every key.
  useEffect(() => {
    const t = setTimeout(() => { if (search !== (params.get("search") || "")) setFilter("search", search.trim()); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const pages = Math.max(1, Math.ceil(total / rowsPerPage));
  const goTo = (p) => { const n = new URLSearchParams(params); if (p > 1) n.set("page", String(p)); else n.delete("page"); setParams(n); };
  const anyFilter = FILTER_KEYS.some((k) => filters[k]);

  return (
    <div className="ld-page">
      <div className="ld-head">
        <div>
          <h1 className="ld-title">Notification log</h1>
          <p className="ld-subtitle">{total.toLocaleString("en-IN")} message{total === 1 ? "" : "s"} sent to leads · latest first</p>
        </div>
        <div className="ld-head-actions">
          <button type="button" className="ld-btn" disabled={job?.busy} onClick={refreshStatus} title="Ask UraanWapp for the current delivery of every sent message (the filters apply)">↻ Refresh status</button>
          <button type="button" className="ld-btn ld-btn-danger" disabled={job?.busy} onClick={resendAll} title="Send every failed or undelivered message again (the filters apply)">Resend failed…</button>
          <Link className="ld-btn" to="/leads">Dashboard</Link>
          <Link className="ld-btn" to="/leads/list">All leads</Link>
        </div>
      </div>

      <div className="ld-filters">
        <div className="ld-filter" style={{ minWidth: 240 }}>
          <span className="ld-filter-label">Lead</span>
          <input className="ld-input" placeholder="Name, mobile or email" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="ld-filter" style={{ minWidth: 150 }}>
          <span className="ld-filter-label">Channel</span>
          <select className="ld-select" value={filters.channel} onChange={(e) => setFilter("channel", e.target.value)}>
            <option value="">WhatsApp and email</option><option value="whatsapp">WhatsApp</option><option value="email">Email</option>
          </select>
        </div>
        <div className="ld-filter" style={{ minWidth: 190 }}>
          <span className="ld-filter-label">Result</span>
          <select className="ld-select" value={filters.result} onChange={(e) => setFilter("result", e.target.value)}>
            {RESULTS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </div>
        <div className="ld-filter" style={{ minWidth: 160 }}>
          <span className="ld-filter-label">Type</span>
          <select className="ld-select" value={filters.kind} onChange={(e) => setFilter("kind", e.target.value)}>
            <option value="">Any type</option>
            {Object.entries(MESSAGE_KIND).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
        <div className="ld-filter" style={{ minWidth: 150 }}>
          <span className="ld-filter-label">From</span>
          <input type="date" className="ld-input" value={filters.from} onChange={(e) => setFilter("from", e.target.value)} />
        </div>
        <div className="ld-filter" style={{ minWidth: 150 }}>
          <span className="ld-filter-label">to</span>
          <input type="date" className="ld-input" value={filters.to} onChange={(e) => setFilter("to", e.target.value)} />
        </div>
        {anyFilter && <button type="button" className="ld-btn" onClick={() => { setSearch(""); setParams(new URLSearchParams()); }}>Clear filters</button>}
      </div>

      {error && <div className="ld-alert ld-alert-error">{error}</div>}
      {job && (
        <div className={`ld-alert ld-alert-${job.tone}`} role="status" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {job.busy && <span className="ld-spinner" aria-hidden="true" />}
          <span style={{ flex: 1 }}>{job.text}</span>
          {!job.busy && <button type="button" className="ld-btn ld-btn-sm" onClick={() => setJob(null)}>Dismiss</button>}
        </div>
      )}

      <div className="ld-card">
        <div className="ld-table-wrap">
          <table className="ld-table">
            <thead><tr><th>Channel</th><th>When</th><th>Lead</th><th>Message</th><th>Result</th><th>By</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {rows.map((m) => {
                const o = outcome(m);
                const reason = m.error || m.deliveryFailure;
                return (
                  <tr key={m._id} className={`ld-msg-row is-${m.channel}`} onClick={() => m.lead && navigate(`/leads/${m.lead._id}`)} style={{ cursor: m.lead ? "pointer" : "default" }}>
                    <td><ChannelBadge channel={m.channel} /></td>
                    <td className="ld-sub" style={{ whiteSpace: "nowrap" }}>{fmtDateTime(m.at)}</td>
                    <td>
                      {m.lead ? (
                        <>
                          <div className="ld-name">{m.lead.fullName}{m.lead.isDeleted ? <span className="ld-sub"> (deleted)</span> : ""}</div>
                          <div className="ld-sub">{m.to || (m.channel === "email" ? m.lead.email : m.lead.mobile) || "—"}{m.lead.courseTitle ? ` · ${m.lead.courseTitle}` : ""}</div>
                        </>
                      ) : <span className="ld-sub">Lead removed</span>}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }}>
                      <div className="ld-name">{m.title || "Message"}</div>
                      <div className="ld-sub">{MESSAGE_KIND[m.kind] || m.kind}{m.templateName ? ` · ${m.templateName}` : ""}{m.attachment?.fileName ? ` · 📎 ${m.attachment.fileName}` : ""}</div>
                      {m.preview && <details><summary className="ld-sub" style={{ cursor: "pointer" }}>Show message</summary><div className="ld-msg-preview">{m.preview}</div></details>}
                    </td>
                    <td>
                      <span className="ld-msg-status" data-s={o.s}>{o.label}</span>
                      {reason && o.s === "failed" && <div className="ld-field-error" style={{ maxWidth: 260 }}>{reason}</div>}
                      {m.resentAt && !m.resend && <div className="ld-sub">↻ Resent {fmtDateTime(m.resentAt)}</div>}
                      {m.resend && (() => {
                        const r = outcome(m.resend);
                        const why = m.resend.error || m.resend.deliveryFailure || m.resend.skipReason;
                        return (
                          <div className="ld-resend">
                            <span className="ld-sub">↻ Resent {fmtDateTime(m.resend.at || m.resentAt)}:</span>{" "}
                            <span className="ld-msg-status" data-s={r.s}>{r.label}</span>
                            {why && r.s === "failed" && <div className="ld-field-error" style={{ maxWidth: 260 }}>{why}</div>}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="ld-sub">{m.by || "System"}</td>
                    <td onClick={(e) => e.stopPropagation()} style={{ cursor: "default", whiteSpace: "nowrap" }}>
                      {outcome(m.resend || m).s === "failed" && m.lead && !m.lead.isDeleted && (
                        <button type="button" className="ld-btn ld-btn-sm" disabled={!!resendingId || job?.busy} onClick={() => resendOne(m)}
                          title={`Send this ${m.channel === "email" ? "email" : "WhatsApp message"} again now`}>
                          {resendingId === m._id ? "Sending…" : m.resend || m.resentAt ? "Resend again" : "Resend"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length && <div className="ld-empty">{loading ? "Loading…" : anyFilter ? "No messages match these filters." : "No messages sent yet."}</div>}
        </div>
        {total > 0 && (
          <div className="ld-pager">
            <span>{(page - 1) * rowsPerPage + 1}–{Math.min(page * rowsPerPage, total)} of {total.toLocaleString("en-IN")} · Page {page} of {pages}</span>
            <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label className="ld-sub" htmlFor="ld-log-rows">Rows per page</label>
              <select id="ld-log-rows" className="ld-select" style={{ width: "auto" }} value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); goTo(1); }}>
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <button type="button" className="ld-btn ld-btn-sm" disabled={page <= 1} onClick={() => goTo(page - 1)}>Previous</button>
              <button type="button" className="ld-btn ld-btn-sm" disabled={page >= pages} onClick={() => goTo(page + 1)}>Next</button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadNotificationLog;
